import { FC, useMemo } from "react";
import { ScrollView } from "react-native";
import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Box, Center, Text } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";
import { HistogramDataPoint } from "@/types/resources";

interface HistogramProps {
  data: HistogramDataPoint[];
  /** Custom formatter for the bin label shown on the x-axis (and in the
      tooltip). Receives the bin's lower bound in the same units as
      `data[i].x`. Defaults to a numeric `toFixed` based on the value
      range — fine for raw counts, but callers rendering monetary or
      otherwise unit-aware data should pipe in a locale-aware formatter
      (e.g. `Intl.NumberFormat` with `style: "currency"`) so axis ticks
      stop being bare numbers. */
  formatLabel?: (binStart: number) => string;
}

const TICK_MUTED = { fontSize: 11, fill: "hsl(220,10%,46%)" };
const GRID_STROKE = "hsl(220,14%,93%)";

function HistogramTooltip({
  active,
  payload,
  primary,
  countLabel,
}: {
  active?: boolean;
  countLabel: string;
  payload?: { payload: { count: number; label: string } }[];
  primary: string;
}) {
  if (!active || !payload?.length) return null;
  const { count, label } = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </p>
      <p style={{ color: primary, fontSize: 12, margin: "2px 0 0" }}>
        {countLabel}: {count}
      </p>
    </div>
  );
}

const Histogram: FC<HistogramProps> = ({ data, formatLabel }) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const { binnedData, chartWidth } = useMemo(() => {
    if (data.length === 0) {
      return { binnedData: [], chartWidth: 400, decimalPlaces: 0 };
    }

    const numBins = Math.min(Math.ceil(Math.log2(data.length) + 1), 15);
    const width = numBins > 5 ? 400 + 100 * (numBins - 5) : 400;

    const values = data.map((d) => d.x);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    let decimals = 0;
    if (range < 1) decimals = 2;
    else if (range < 5) decimals = 1;

    const fallbackLabel = (binStart: number) => binStart.toFixed(decimals);
    const labelFor = formatLabel ?? fallbackLabel;

    const binSize = range / numBins;
    const bins = Array.from({ length: numBins }, (_, i) => {
      const binStart = min + i * binSize;
      const binEnd = min + (i + 1) * binSize;
      const count = values.filter(
        (v) => v >= binStart && (i === numBins - 1 ? v <= binEnd : v < binEnd)
      ).length;
      return { count, label: labelFor(binStart) };
    });

    return { binnedData: bins, chartWidth: width, decimalPlaces: decimals };
  }, [data, formatLabel]);

  if (binnedData.length === 0) {
    return (
      <Center className="h-[450px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const countLabel = i18n.t("dashboardNavigator.count");

  const sharedChartProps = {
    barCategoryGap: 2,
    data: binnedData,
    margin: { bottom: 20, left: 10, right: 20, top: 20 },
  };

  const sharedChildren = (
    <>
      <CartesianGrid
        stroke={GRID_STROKE}
        strokeDasharray="3 3"
        vertical={false}
      />
      <XAxis dataKey="label" tick={TICK_MUTED} tickLine={false} />
      <YAxis
        allowDecimals={false}
        axisLine={false}
        tick={TICK_MUTED}
        tickCount={5}
        tickFormatter={(v) => String(Math.floor(v))}
        tickLine={false}
      />
      <Tooltip
        content={
          <HistogramTooltip
            countLabel={countLabel}
            primary={themeColors.primary}
          />
        }
        cursor={{ fill: "hsl(220,14%,97%)" }}
      />
      <Bar dataKey="count" fill={themeColors.primary} radius={[4, 4, 0, 0]} />
    </>
  );

  const needsScroll = chartWidth > 500;

  return (
    <Box className="h-[450px] w-full">
      {needsScroll ? (
        <ScrollView horizontal>
          <RechartsBarChart
            {...sharedChartProps}
            height={420}
            width={chartWidth}
          >
            {sharedChildren}
          </RechartsBarChart>
        </ScrollView>
      ) : (
        <ResponsiveContainer height={420} width="100%">
          <RechartsBarChart {...sharedChartProps}>
            {sharedChildren}
          </RechartsBarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default Histogram;
